import React from 'react';
import { useIntl } from 'react-intl';

import { SharedHeaderProps } from '../../../types/SharedHeaderProps';
import { Card, Props as CardProps } from '../Card';

interface Props extends CardProps {
  /** Header must be provided, will serve as the `summary` of a `details` card */
  header: SharedHeaderProps;
  /** The collapsed state of the card */
  isCollapsed: boolean;
  /** Controlled callback to update collapse */
  onToggle: (updatedIsCollapsed: boolean) => void;
  /** Convenient passthrough configuration to transform back to a regular card */
  disableCollapse?: boolean;
  /** Position of the collapse icon - 'left' or 'right' */
  togglePosition?: 'left' | 'right';
  /** Action to perform when the collapse icon is clicked */
  onToggleAction?: () => void;
}

export const CollapsibleCard = ({
  children,
  disableCollapse,
  header,
  isCollapsed,
  togglePosition = 'right',
  onToggle,
  onToggleAction,
  ...rest
}: Props) => {
  const intl = useIntl();

  const toggleAction = {
    icon: isCollapsed ? ('chevron-down' as const) : ('chevron-up' as const),
    mode: 'icon' as const,
    text: isCollapsed
      ? intl.formatMessage({
          id: 'CollapsibleCard.expand',
          defaultMessage: 'Show',
          description: 'Text for the expand action of a collapsible card',
        })
      : intl.formatMessage({
          id: 'CollapsibleCard.collapse',
          defaultMessage: 'Hide',
          description: 'Text for the collapse action of a collapsible card',
        }),
  };

  const cardProps = disableCollapse
    ? {
        ...rest,
        children,
        header,
      }
    : {
        ...rest,
        as: 'details' as const,
        children,
        domProps: {
          open: !isCollapsed,
        },

        header: {
          ...header,
          toggleAction: togglePosition === 'left' ? toggleAction : undefined,
          actions: [
            ...(header.actions ?? []),
            ...(togglePosition === 'right' ? [toggleAction] : []),
          ],
          as: 'summary',
          onClick: (event?: MouseEvent) => {
            event?.preventDefault();
            onToggle(!isCollapsed);
          },
        },
      };

  return <Card {...cardProps} />;
};
